import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  deleteLscScheduleSource,
  getLscScheduleSource,
  putLscScheduleSource,
  setLscAutoSyncSafeApply,
  syncLscScheduleSource,
} from '../api';
import type { LscScheduleSource, LscScheduleSourceSyncResult } from '../types';
import { lscPath } from '../routing';
import LscMutationErrorAlert from './LscMutationErrorAlert';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';

export type ScheduleSourcePanelProps = {
  leagueId: string;
  leagueArchived?: boolean;
  onMutationSuccess?: () => void;
  /** details keeps the saved URL, save, and disconnect. The import page owns status and sync. */
  placement?: 'full' | 'details';
};

function formatTimestamp(value?: string | null): string {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

export const SCHEDULE_SOURCE_SAVE_SUCCESS_TITLE = 'Schedule source saved';
export const SCHEDULE_SOURCE_SAVE_SUCCESS_DETAIL =
  'LSC will use this NeonSportz source for future schedule syncs.';

function statusLabel(source?: LscScheduleSource | null): string {
  if (!source?.enabled) return 'Not connected';
  if (source.sync_status === 'out_of_sync') return 'Out of sync';
  if (source.sync_status === 'needs_attention') return 'Needs attention';
  if (source.sync_status === 'failed') return 'Sync failed';
  if (source.sync_status === 'syncing') return 'Syncing…';
  return 'Schedule source connected';
}

export function ScheduleSourcePanelView({
  leagueId,
  source,
  activeStageLabel,
  detectedStageLabel,
  pilotEnabled,
  safeApplyAvailable = true,
  url,
  saving,
  syncing,
  disconnecting,
  error,
  saveSuccess,
  syncPreview,
  onUrlChange,
  onSave,
  onSync,
  onDisconnect,
  onToggleSafeApply,
  safeApplyEnabled,
  safeApplySaving,
  leagueArchived = false,
  detailsOnly = false,
}: {
  leagueId: string;
  source: LscScheduleSource | null;
  activeStageLabel?: string | null;
  detectedStageLabel?: string | null;
  pilotEnabled: boolean;
  /** Backend kill switch LSC_AUTO_SYNC_SAFE_APPLY_ENABLED. */
  safeApplyAvailable?: boolean;
  url: string;
  saving: boolean;
  syncing: boolean;
  disconnecting: boolean;
  error: ParsedLscMutationError | null;
  saveSuccess: boolean;
  syncPreview: LscScheduleSourceSyncResult | null;
  onUrlChange: (value: string) => void;
  onSave: () => void;
  onSync: () => void;
  onDisconnect: () => void;
  onToggleSafeApply?: (enabled: boolean) => void;
  safeApplyEnabled?: boolean;
  safeApplySaving?: boolean;
  leagueArchived?: boolean;
  detailsOnly?: boolean;
}) {
  const connected = Boolean(source?.enabled);

  return (
    <div className="card space-y-4" data-testid="schedule-source-panel">
      <div>
        <h2 className="text-xl font-bold text-white">Schedule Source</h2>
        <p className="text-sm text-secondary-400 mt-1">
          Connect NeonSportz once. LSC will use this saved source for future schedule syncs.
        </p>
      </div>

      {!pilotEnabled ? (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-3 space-y-2"
          data-testid="schedule-source-pilot-disabled"
        >
          <p className="text-sm text-secondary-300">
            Not enabled for this league yet.
          </p>
          <p className="text-sm text-secondary-400">
            Automatic schedule sync and keep-current are limited to enabled leagues. Use
            schedule import for now, or contact support if you need this source enabled.
          </p>
          <Link
            to={lscPath(`/leagues/${leagueId}/import`)}
            className="text-sm font-medium text-primary-300 hover:text-primary-200"
          >
            Use schedule import
          </Link>
        </div>
      ) : null}

      {error ? <LscMutationErrorAlert error={error} /> : null}

      <div className="space-y-2">
        <label htmlFor="schedule-source-url" className="text-sm font-medium text-secondary-200">
          NeonSportz league URL
        </label>
        <input
          id="schedule-source-url"
          type="url"
          value={url}
          onChange={(event) => onUrlChange(event.target.value)}
          placeholder="https://neonsportz.com/leagues/your-league"
          className="input-field text-slate-950 placeholder:text-secondary-500 disabled:bg-secondary-100 disabled:text-secondary-500 dark:text-white dark:placeholder-secondary-400"
          disabled={!pilotEnabled || leagueArchived}
          data-testid="schedule-source-url-input"
        />
      </div>

      <dl className="grid gap-3 sm:grid-cols-2 text-sm">
        <div>
          <dt className="text-secondary-500">Status</dt>
          <dd className="text-secondary-100" data-testid="schedule-source-status">
            {statusLabel(source)}
          </dd>
        </div>
        <div>
          <dt className="text-secondary-500">Last sync</dt>
          <dd className="text-secondary-100" data-testid="schedule-source-last-sync">
            {formatTimestamp(source?.last_sync_at)}
          </dd>
        </div>
        {detailsOnly ? null : (
          <div>
            <dt className="text-secondary-500">LSC active week</dt>
            <dd className="text-secondary-100" data-testid="schedule-source-lsc-week">
              {activeStageLabel || '—'}
            </dd>
          </div>
        )}
        {detailsOnly ? null : (
          <div>
            <dt className="text-secondary-500">NeonSportz detected week</dt>
            <dd className="text-secondary-100" data-testid="schedule-source-detected-week">
              {detectedStageLabel || source?.last_detected_week || '—'}
            </dd>
          </div>
        )}
      </dl>

      {!detailsOnly && connected && detectedStageLabel && activeStageLabel && detectedStageLabel !== activeStageLabel ? (
        <p className="text-sm text-amber-200" data-testid="schedule-source-out-of-sync-copy">
          NeonSportz shows {detectedStageLabel}. LSC is currently on {activeStageLabel}.
        </p>
      ) : null}

      {syncPreview ? (
        <div className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-4 space-y-2 text-sm">
          <p className="font-medium text-white">Sync preview</p>
          <p className="text-secondary-300">{syncPreview.summary_copy}</p>
          {syncPreview.hold_reasons && syncPreview.hold_reasons.length > 0 ? (
            <ul className="list-disc list-inside text-amber-200">
              {syncPreview.hold_reasons.map((hold) => (
                <li key={hold}>{hold.replace(/_/g, ' ')}</li>
              ))}
            </ul>
          ) : null}
          <p className="text-secondary-400">Review changes before applying.</p>
        </div>
      ) : null}

      {saveSuccess ? (
        <div
          className="rounded-lg border border-emerald-700/60 bg-emerald-900/20 p-3 text-sm"
          role="status"
          data-testid="schedule-source-save-success"
        >
          <p className="font-medium text-emerald-100">{SCHEDULE_SOURCE_SAVE_SUCCESS_TITLE}</p>
          <p className="text-emerald-200/90 mt-1">{SCHEDULE_SOURCE_SAVE_SUCCESS_DETAIL}</p>
        </div>
      ) : null}

      {!detailsOnly && connected && pilotEnabled && safeApplyAvailable ? (
        <label
          className="flex items-start gap-3 rounded-lg border border-secondary-700/60 bg-secondary-900/40 p-3 cursor-pointer"
          data-testid="schedule-source-safe-apply-toggle"
        >
          <input
            type="checkbox"
            className="mt-1"
            checked={Boolean(safeApplyEnabled)}
            disabled={Boolean(safeApplySaving) || leagueArchived || !onToggleSafeApply}
            onChange={(event) => onToggleSafeApply?.(event.target.checked)}
            data-testid="schedule-source-safe-apply-checkbox"
          />
          <span>
            <span className="block text-sm font-medium text-secondary-100">
              Keep my schedule current automatically (scores and week changes only)
            </span>
            <span className="block text-xs text-secondary-400 mt-1">
              Safe updates apply on their own. Season changes, removals, and team swaps still wait
              for your review.
            </span>
          </span>
        </label>
      ) : null}

      {!detailsOnly && connected && pilotEnabled && !safeApplyAvailable ? (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-3 text-sm text-secondary-400"
          data-testid="schedule-source-safe-apply-kill-switch-off"
        >
          Automatic keep-current is not available right now. Manual sync still works.
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary"
          onClick={onSave}
          disabled={!pilotEnabled || saving || !url.trim() || leagueArchived}
          data-testid="schedule-source-save"
        >
          {saving ? 'Saving…' : connected ? 'Update source' : 'Save source'}
        </button>
        {connected ? (
          <>
            {detailsOnly ? null : (
              <button
                type="button"
                className="btn-secondary"
                onClick={onSync}
                disabled={syncing || leagueArchived}
                data-testid="schedule-source-sync"
              >
                {syncing ? 'Syncing…' : 'Sync from NeonSportz'}
              </button>
            )}
            <button
              type="button"
              className="btn-secondary text-red-200"
              onClick={onDisconnect}
              disabled={disconnecting || leagueArchived}
              data-testid="schedule-source-disconnect"
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </>
        ) : null}
        {detailsOnly ? null : (
          <Link
            to={lscPath(`/leagues/${leagueId}/import`)}
            className="btn-secondary inline-flex items-center"
          >
            Advanced import
          </Link>
        )}
      </div>
    </div>
  );
}

const ScheduleSourcePanel: React.FC<ScheduleSourcePanelProps> = ({
  leagueId,
  leagueArchived = false,
  onMutationSuccess,
  placement = 'full',
}) => {
  const [source, setSource] = useState<LscScheduleSource | null>(null);
  const [activeStageLabel, setActiveStageLabel] = useState<string | null>(null);
  const [detectedStageLabel, setDetectedStageLabel] = useState<string | null>(null);
  const [pilotEnabled, setPilotEnabled] = useState(false);
  const [safeApplyAvailable, setSafeApplyAvailable] = useState(true);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [syncPreview, setSyncPreview] = useState<LscScheduleSourceSyncResult | null>(null);
  const [safeApplyEnabled, setSafeApplyEnabled] = useState(false);
  const [safeApplySaving, setSafeApplySaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLscScheduleSource(leagueId);
      setSource(data.schedule_source ?? null);
      setActiveStageLabel(data.active_stage_label ?? null);
      setDetectedStageLabel(
        data.schedule_source?.last_detected_week ?? data.schedule_source?.last_detected_season ?? null
      );
      setPilotEnabled(Boolean(data.pilot_enabled));
      setSafeApplyAvailable(
        data.auto_sync_safe_apply_available === undefined
          ? true
          : Boolean(data.auto_sync_safe_apply_available)
      );
      setSafeApplyEnabled(Boolean(data.auto_sync_safe_apply_enabled));
      if (data.schedule_source?.source_url) {
        setUrl(data.schedule_source.source_url);
      }
    } catch (err) {
      setError(parseLscMutationError(err, 'Schedule source unavailable.'));
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    setSyncPreview(null);
    try {
      const data = await putLscScheduleSource(leagueId, url.trim());
      setSource(data.schedule_source ?? null);
      setActiveStageLabel(data.active_stage_label ?? null);
      setDetectedStageLabel(data.schedule_source?.last_detected_week ?? null);
      setSaveSuccess(true);
      onMutationSuccess?.();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not save schedule source.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSaveSuccess(false);
    setSyncPreview(null);
    try {
      const preview = await syncLscScheduleSource(leagueId);
      setSyncPreview(preview);
      setSource(preview.schedule_source ?? source);
      setActiveStageLabel(preview.active_stage_label ?? activeStageLabel);
      setDetectedStageLabel(preview.detected_stage_label ?? null);
    } catch (err) {
      setError(parseLscMutationError(err, 'Schedule sync preview failed.'));
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const data = await deleteLscScheduleSource(leagueId);
      setSource(data.schedule_source ?? null);
      setSafeApplyEnabled(false);
      onMutationSuccess?.();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not disconnect schedule source.'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleToggleSafeApply = async (enabled: boolean) => {
    setSafeApplySaving(true);
    setError(null);
    try {
      const data = await setLscAutoSyncSafeApply(leagueId, enabled);
      setSafeApplyEnabled(Boolean(data.auto_sync_safe_apply_enabled));
      setSource(data.schedule_source ?? source);
      onMutationSuccess?.();
    } catch (err) {
      setError(parseLscMutationError(err, 'Could not update automatic schedule updates.'));
    } finally {
      setSafeApplySaving(false);
    }
  };

  if (loading) {
    return (
      <p className="text-sm text-secondary-400" data-testid="schedule-source-loading">
        Loading schedule source…
      </p>
    );
  }

  return (
    <ScheduleSourcePanelView
      leagueId={leagueId}
      source={source}
      activeStageLabel={activeStageLabel}
      detectedStageLabel={detectedStageLabel}
      pilotEnabled={pilotEnabled}
      safeApplyAvailable={safeApplyAvailable}
      url={url}
      saving={saving}
      syncing={syncing}
      disconnecting={disconnecting}
      error={error}
      saveSuccess={saveSuccess}
      syncPreview={syncPreview}
      onUrlChange={(value) => {
        setSaveSuccess(false);
        setUrl(value);
      }}
      onSave={() => {
        void handleSave();
      }}
      onSync={() => {
        void handleSync();
      }}
      onDisconnect={() => {
        void handleDisconnect();
      }}
      onToggleSafeApply={(enabled) => {
        void handleToggleSafeApply(enabled);
      }}
      safeApplyEnabled={safeApplyEnabled}
      safeApplySaving={safeApplySaving}
      leagueArchived={leagueArchived}
      detailsOnly={placement === 'details'}
    />
  );
};

export default ScheduleSourcePanel;
